import { createFocusTrap, prefersReducedMotion, safeFocus } from '@nulo/a11y';

/**
 * @extends {HTMLElement}
 */
class NuloVideoModal extends HTMLElement {
  /** @type {(() => void) | null} */
  #releaseTrap = null;

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.hidden = true;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#bindTriggers(signal);
    this.#bindCloseControls(signal);
  }

  disconnectedCallback() {
    this.#close();

    this.#releaseTrap?.();
    this.#releaseTrap = null;

    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindTriggers(signal) {
    const modalId = this.id;
    if (!modalId) return;

    const root = this.closest('.shopify-section') || this.parentElement;
    if (!root) return;

    const triggers = root.querySelectorAll(
      `[aria-controls="${modalId}"][data-nulo-video-open]`
    );

    for (const trigger of triggers) {
      if (!(trigger instanceof HTMLElement)) continue;

      trigger.addEventListener('click', () => this.#open(trigger), { signal });
    }
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindCloseControls(signal) {
    const closeBtn = this.querySelector('[data-nulo-video-close]');
    closeBtn?.addEventListener('click', () => this.#close(), { signal });

    this.addEventListener(
      'click',
      (event) => {
        if (event.target === this) this.#close();
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && !this.hidden) this.#close();
      },
      { signal }
    );
  }

  /**
   * @param {HTMLElement} trigger
   */
  #open(trigger) {
    this.#trigger = trigger;
    this.hidden = false;
    document.body.classList.add('nulo-video-modal-open');

    this.#releaseTrap?.();
    this.#releaseTrap = createFocusTrap(this, () => this.#close());

    const video = this.querySelector('.nulo-video-modal__video');
    if (
      video instanceof HTMLMediaElement &&
      this.dataset.autoplay === 'true' &&
      !prefersReducedMotion()
    ) {
      video.muted = true;
      if (typeof video.play === 'function') {
        video.play().catch(() => {});
      }
    }

    const closeBtn = this.querySelector('[data-nulo-video-close]');
    if (closeBtn instanceof HTMLElement) safeFocus(closeBtn);
  }

  #close() {
    this.#releaseTrap?.();
    this.#releaseTrap = null;

    this.hidden = true;
    document.body.classList.remove('nulo-video-modal-open');

    const video = this.querySelector('.nulo-video-modal__video');
    if (video instanceof HTMLMediaElement) {
      video.pause();
      video.currentTime = 0;
    }

    if (this.#trigger) {
      safeFocus(this.#trigger);
      this.#trigger = null;
    }
  }
}

if (!customElements.get('nulo-video-modal')) {
  customElements.define('nulo-video-modal', NuloVideoModal);
}
